/**
 * 実績定義
 *
 * 各マップごとの実績を定義。
 * metadata は GameEndScreen から渡される各マップの計算済みデータ。
 */

export interface Achievement {
  id: string;
  mapType: string;
  name: string;
  description: string;
  icon: string;
  checkCondition: (metadata: ScoreMetadata) => boolean;
}

/** スコア登録時のメタデータ（全マップ共通） */
export interface ScoreMetadata {
  sharesIssued: number;
  [key: string]: unknown;
}

export const ACHIEVEMENTS: Achievement[] = [
  // =========================================================================
  // Barbados（7実績）
  // =========================================================================

  // スコア系
  {
    id: 'barbados_cash60',
    mapType: 'barbados',
    name: 'カリブ海の貿易王',
    description: '最終スコア60以上でクリア',
    icon: '👑',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 60,
  },
  {
    id: 'barbados_cash40',
    mapType: 'barbados',
    name: 'パイナップル帝国',
    description: '最終スコア40以上でクリア',
    icon: '🍍',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 40,
  },
  {
    id: 'barbados_cash20',
    mapType: 'barbados',
    name: '有能な商人',
    description: '最終スコア20以上でクリア',
    icon: '🏪',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 20,
  },
  {
    id: 'barbados_first_win',
    mapType: 'barbados',
    name: '初心者卒業',
    description: '破産せずにクリア',
    icon: '🎓',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 0,
  },
  // 制限プレイ系
  {
    id: 'barbados_shares6',
    mapType: 'barbados',
    name: '慎重な商人',
    description: '発行株式数6以下でクリア',
    icon: '💎',
    checkCondition: (m) => m.sharesIssued <= 6 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'barbados_shares4',
    mapType: 'barbados',
    name: '無借金経営',
    description: '発行株式数4以下でクリア',
    icon: '🏦',
    checkCondition: (m) => m.sharesIssued <= 4 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'barbados_no_undo',
    mapType: 'barbados',
    name: '完璧なプレイ',
    description: 'Undoなしでクリア',
    icon: '✨',
    checkCondition: (m) => (m.undoCount as number) === 0 && (m.unifiedFinalScore as number) >= 0,
  },

  // =========================================================================
  // Detroit Bankruptcy Solo（7実績）
  // =========================================================================

  // ターン生存系
  {
    id: 'detroit_turn25',
    mapType: 'detroit_bankruptcy_solo',
    name: '猫を揉む',
    description: '25ターン生存',
    icon: '🐱',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 25,
  },
  {
    id: 'detroit_turn20',
    mapType: 'detroit_bankruptcy_solo',
    name: '不屈の精神',
    description: '20ターン生存',
    icon: '🔥',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 20,
  },
  {
    id: 'detroit_turn15',
    mapType: 'detroit_bankruptcy_solo',
    name: '大統領',
    description: '15ターン生存',
    icon: '🏛️',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 15,
  },
  {
    id: 'detroit_turn12',
    mapType: 'detroit_bankruptcy_solo',
    name: '殿堂入り',
    description: '12ターン生存',
    icon: '🏆',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 12,
  },
  {
    id: 'detroit_turn10',
    mapType: 'detroit_bankruptcy_solo',
    name: 'ロック・シティ',
    description: '10ターン以上生存',
    icon: '🎸',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 10,
  },
  {
    id: 'detroit_turn8',
    mapType: 'detroit_bankruptcy_solo',
    name: '平均以上',
    description: '8ターン以上生存',
    icon: '📊',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 8,
  },
  {
    id: 'detroit_no_undo',
    mapType: 'detroit_bankruptcy_solo',
    name: '一発勝負',
    description: 'Undoなしで勝利',
    icon: '✨',
    checkCondition: (m) => (m.undoCount as number) === 0 && (m.unifiedFinalScore as number) >= 7,
  },

  // =========================================================================
  // Puerto Rico（8実績）
  // =========================================================================

  // スコア系
  {
    id: 'puerto_rico_score70',
    mapType: 'puerto_rico',
    name: '伝説の総督',
    description: '最終スコア70以上でクリア',
    icon: '👑',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 70,
  },
  {
    id: 'puerto_rico_score50',
    mapType: 'puerto_rico',
    name: '島の繁栄者',
    description: '最終スコア50以上でクリア',
    icon: '🏝️',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 50,
  },
  {
    id: 'puerto_rico_score30',
    mapType: 'puerto_rico',
    name: '堅実な統治',
    description: '最終スコア30以上でクリア',
    icon: '📈',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 30,
  },
  {
    id: 'puerto_rico_first_win',
    mapType: 'puerto_rico',
    name: '初心者卒業',
    description: '黒キューブの災禍を乗り越えてクリア',
    icon: '🎓',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 0,
  },
  // 特殊・制限系
  {
    id: 'puerto_rico_clean_island',
    mapType: 'puerto_rico',
    name: '島の浄化者',
    description: '黒キューブをすべて運んでクリア',
    icon: '🧹',
    checkCondition: (m) => (m.remainingBlackCubes as number) === 0 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'puerto_rico_cubes3',
    mapType: 'puerto_rico',
    name: '効率的な配送',
    description: '残りキューブ合計3個以下でクリア',
    icon: '📦',
    checkCondition: (m) => (m.remainingTotalCubes as number) <= 3 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'puerto_rico_shares13',
    mapType: 'puerto_rico',
    name: '節約家の総督',
    description: '発行株式数13以下でクリア',
    icon: '💎',
    checkCondition: (m) => m.sharesIssued <= 13 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'puerto_rico_shares10',
    mapType: 'puerto_rico',
    name: '清廉な支配者',
    description: '発行株式数10以下でクリア',
    icon: '🏦',
    checkCondition: (m) => m.sharesIssued <= 10 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'puerto_rico_no_undo',
    mapType: 'puerto_rico',
    name: '熟練の航海',
    description: 'Undoなしでクリア',
    icon: '✨',
    checkCondition: (m) => (m.undoCount as number) === 0 && (m.unifiedFinalScore as number) >= 0,
  },

  // =========================================================================
  // Seattle Metro Solo（13実績）
  // =========================================================================

  // スコア系
  {
    id: 'seattle_score20',
    mapType: 'seattle_metro_solo',
    name: '伝説の都市計画者',
    description: '最終スコア20以上でクリア',
    icon: '👑',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 20,
  },
  {
    id: 'seattle_score15',
    mapType: 'seattle_metro_solo',
    name: '未来都市',
    description: '最終スコア15以上でクリア',
    icon: '🏙️',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 15,
  },
  {
    id: 'seattle_score10',
    mapType: 'seattle_metro_solo',
    name: '世界最高水準',
    description: '最終スコア10以上でクリア',
    icon: '🌟',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 10,
  },
  {
    id: 'seattle_score5',
    mapType: 'seattle_metro_solo',
    name: '平均的交通網',
    description: '最終スコア5以上でクリア',
    icon: '🚂',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 5,
  },
  {
    id: 'seattle_score0',
    mapType: 'seattle_metro_solo',
    name: 'なんとか達成',
    description: 'スコア0以上でクリア（勝利）',
    icon: '✅',
    checkCondition: (m) => (m.unifiedFinalScore as number) >= 0,
  },
  // 制限プレイ系
  {
    id: 'seattle_all_connected',
    mapType: 'seattle_metro_solo',
    name: '取りこぼしなし',
    description: 'すべてのCity/Townを接続してクリア',
    icon: '🗺️',
    checkCondition: (m) => (m.unconnectedStations as number) === 0 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_loco5',
    mapType: 'seattle_metro_solo',
    name: '省エネ走行',
    description: '機関車レベル5以下でクリア',
    icon: '🚃',
    checkCondition: (m) => (m.locomotiveLevel as number) <= 5 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_loco3',
    mapType: 'seattle_metro_solo',
    name: '蒸気機関の達人',
    description: '機関車レベル3以下でクリア',
    icon: '⚡',
    checkCondition: (m) => (m.locomotiveLevel as number) <= 3 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_cargo4',
    mapType: 'seattle_metro_solo',
    name: '効率的な物流',
    description: '貨物輸送レベル4以下でクリア',
    icon: '📦',
    checkCondition: (m) => (m.cargoTransportLevel as number) <= 4 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_cargo3',
    mapType: 'seattle_metro_solo',
    name: '最小輸送',
    description: '貨物輸送レベル3以下でクリア',
    icon: '🎯',
    checkCondition: (m) => (m.cargoTransportLevel as number) <= 3 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_shares10',
    mapType: 'seattle_metro_solo',
    name: '節約家',
    description: '発行株式数10以下でクリア',
    icon: '💰',
    checkCondition: (m) => m.sharesIssued <= 10 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_shares7',
    mapType: 'seattle_metro_solo',
    name: '超節約経営',
    description: '発行株式数7以下でクリア',
    icon: '💎',
    checkCondition: (m) => m.sharesIssued <= 7 && (m.unifiedFinalScore as number) >= 0,
  },
  {
    id: 'seattle_no_undo',
    mapType: 'seattle_metro_solo',
    name: '完璧な都市計画',
    description: 'Undoなしでクリア',
    icon: '✨',
    checkCondition: (m) => (m.undoCount as number) === 0 && (m.unifiedFinalScore as number) >= 0,
  },
];
